import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import { GetMember200ResponseBody } from 'openapi/omise-craft-core-spec';

import userRoleAtom from '@/stores/session/UserRoleAtom';

/**
 * AuthContextType
 * AuthContext向けの型定義
 */
export type AuthContextType = {
  // user_id by application
  uid: string;
  // Amazon Cognito User ID in the user pool
  cid: string;
  // Amazon Cognito JWT token
  accessToken: string;
  // user role number by application
  role: string;
  member: GetMember200ResponseBody | null;
  // 初期化完了フラグ: 認証情報の復元処理が完了しているか
  isAuthInitialized: boolean;
};

/**
 * AuthContextの初期値を返す
 *
 * @returns AuthContextType
 */
export function initialAuthContext(): AuthContextType {
  return {
    uid: '',
    cid: '',
    accessToken: '',
    role: '',
    member: null,
    isAuthInitialized: false
  };
}

/**
 * 型定義用
 */
const AuthContextAtom = atomWithStorage('authContext', initialAuthContext());

/**
 * 認証コンテキストのatom
 */
export type AuthContextAtomType = typeof AuthContextAtom;

/**
 * AuthContextのget only atomを保持するクラス (Singleton)
 */
export class AuthContextSingleton {
  /**
   * @field self instance
   */
  private static thiz: AuthContextSingleton | null = null;

  /**
   * @field 認証コンテキスト
   */
  private authContext: AuthContextType = initialAuthContext();

  /**
   * @field
   * データ保持atom
   */
  private baseAuthContextAtom: AuthContextAtomType = atomWithStorage('authContext', this.authContext);

  /**
   * @field データ操作用atom
   * set利用時にクラス内の `authContext` を書き換える
   * これによって、 `react-hooks` の `component` 外でデータを参照できない問題を解決する
   */
  public authContextAtom = atom(
    (get) => get(this.baseAuthContextAtom),
    (_get, set, value: AuthContextType) => {
      this.authContext.cid = value.cid;
      this.authContext.accessToken = value.accessToken;
      this.authContext.uid = value.uid;
      this.authContext.role = value.role;
      this.authContext.member = value.member;
      this.authContext.isAuthInitialized = value.isAuthInitialized;
      set(this.baseAuthContextAtom, value);
      // 既存ロール関連ロジックのAtomを更新
      set(userRoleAtom, value.role);
    }
  );

  /**
   * @field cognito_idだけを取得するatom
   */
  public readonlyCognitoIdAtom = atom((get) => get(this.baseAuthContextAtom).cid);
  /**
   * @field user_idだけを取得するatom
   */
  public readonlyUserIdAtom = atom((get) => get(this.baseAuthContextAtom).uid);
  /**
   * @field access tokenだけを取得するatom
   */
  public readonlyAccessTokenAtom = atom((get) => get(this.baseAuthContextAtom).accessToken);
  /**
   * @field roleだけを取得するatom
   */
  public readonlyRoleAtom = atom((get) => get(this.baseAuthContextAtom).role);
  /**
   * @field memberだけを取得するatom
   */
  public readonlyMemberAtom = atom((get) => get(this.baseAuthContextAtom).member);
  /**
   * @field isAuthInitializedだけを取得するatom
   */
  public readonlyIsAuthInitializedAtom = atom((get) => get(this.baseAuthContextAtom).isAuthInitialized);

  /**
   * @field cognito_idを書き込むだけのatom
   */
  public writeonlyCognitoIdAtom = atom(this.baseAuthContextAtom, (get, set, value: string) => {
    const current = get(this.baseAuthContextAtom);
    current.cid = value;
    set(this.baseAuthContextAtom, current);
  });
  /**
   * @field access tokenを書き込むだけのatom
   */
  public writeonlyAccessTokenAtom = atom(this.baseAuthContextAtom, (get, set, value: string) => {
    const current = get(this.baseAuthContextAtom);
    current.accessToken = value;
    set(this.baseAuthContextAtom, current);
  });
  /**
   * @field roleを書き込むだけのatom
   */
  public writeonlyRoleAtom = atom(this.baseAuthContextAtom, (get, set, value: string) => {
    const current = get(this.baseAuthContextAtom);
    current.role = value;
    set(this.baseAuthContextAtom, current);
  });
  /**
   * @field memberを書き込むだけのatom
   */
  public writeonlyMemberAtom = atom(this.baseAuthContextAtom, (get, set, value: GetMember200ResponseBody) => {
    const current = get(this.baseAuthContextAtom);
    current.member = value;
    set(this.baseAuthContextAtom, current);
  });

  /**
   * シングルトンインスタンス取得
   * @returns {AuthContextSingleton}
   */
  public static getInstance(): AuthContextSingleton {
    if (AuthContextSingleton.thiz === null) {
      AuthContextSingleton.thiz = new AuthContextSingleton();
    }
    return AuthContextSingleton.thiz;
  }

  /**
   * 認証コンテキストデータ
   * @returns {AuthContextType}
   */
  public getAuthContext(): AuthContextType {
    return this.authContext;
  }
}
